var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import dayjs from 'dayjs';
import login from '@nibfe/mkt-user-api';
import Toast from '@max/leez-toast';
import { TechStackUtil } from '@nibfe/mkt-common-utils';
import { CouponStatus, getCouponStatus, getPresendCoupons } from '@nibfe/mkt-coupon-api';
import { ECouponType, EResultCode } from '../lib/interface';
import reloadPage from "./reloadPage/index.web";
import { hasValidStateStatusList } from '../lib/constant';
/**
 * 根据活动键数组和格式化后的优惠券配置信息，生成活动与业务线的映射关系
 * @param campaignKeyArr 券活动密钥数组
 * @param formatCouponConfigRes 格式化后的优惠券配置信息
 * @returns 活动与业务线的映射关系对象
 */
export const getCampaignBizMap = (campaignKeyArr, formatCouponConfigRes) => {
    const campaignBizMap = {};
    campaignKeyArr.forEach(key => {
        const { nibBiz } = formatCouponConfigRes[key] || {};
        campaignBizMap[key] = nibBiz;
    });
    return campaignBizMap;
};
// 获取券预校验信息及服务器时间
export const presendRequest = (params, formatCouponConfigRes = {}, pageInfo) => __awaiter(void 0, void 0, void 0, function* () {
    const requestStartTime = +new Date();
    const { campaignKeys = [] } = params || {};
    const emptyResult = {
        couponList: [],
        currentTimeMillis: +new Date()
    };
    const isInEditor = TechStackUtil.isInEditor();
    if (!(campaignKeys || []).length)
        return emptyResult;
    const campaignBizMap = getCampaignBizMap(campaignKeys, formatCouponConfigRes);
    const requestParams = Object.assign({ campaignScene: isInEditor ? 0 : 1, 
        // 在编辑器中时初始化不查询膨胀信息，传0允许未登录。
        campaignBizMap }, params);
    const res = yield getPresendCoupons(requestParams);
    const { resultCode, data } = res || {};
    let { detailList, currentTimeMillis } = data || {};
    if (resultCode === EResultCode.UN_LOGIN) {
        const { userId } = yield login.login();
        userId > 0 ? reloadPage({
            rootTag: pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.rootTag
        }) : Toast.open({
            content: '请登录后领券'
        });
        return emptyResult;
    }
    if (resultCode !== 0 || !detailList) {
        throw new Error(JSON.stringify({
            req: requestParams,
            res
        }));
    }
    const requestEndTime = +new Date();
    const requestTime = requestEndTime - requestStartTime;
    currentTimeMillis = currentTimeMillis ? Number(currentTimeMillis) + requestTime / 2 : +new Date();
    let couponList = formatCouponList(data === null || data === void 0 ? void 0 : data.detailList, formatCouponConfigRes, campaignKeys, currentTimeMillis);
    return {
        couponList: couponList || [],
        currentTimeMillis: currentTimeMillis || +new Date()
    };
});
/**
 * 对优惠券列表进行排序
 * @param couponList 优惠券列表
 * @returns 排序后的优惠券列表
 */
export const sortCoupon = (couponList, isDefaultSort = true) => {
    if (!isDefaultSort) {
        return couponList;
    }
    try {
        const statusList = {
            canInflateList: [],
            notClaimedList: [],
            afterInflateOrToUseList: [],
            notStartList: [],
            soldOutList: [],
            usedList: [],
            defaultList: []
        };
        const beforeClaimeStatusList = [CouponStatus.NotStart, CouponStatus.Ended, CouponStatus.SoldOut, CouponStatus.NotClaimed];
        for (let i = 0; i < couponList.length; i++) {
            const couponInfo = couponList[i];
            const { status, valueType, discountAmount, priceLimit } = couponInfo;
            let listKey;
            if (status === CouponStatus.CanInflate) {
                listKey = 'canInflateList';
            }
            else if (status === CouponStatus.NotClaimed) {
                listKey = 'notClaimedList';
            }
            else if ([CouponStatus.AfterInflate, CouponStatus.Claimed, CouponStatus.ToUse].includes(status)) {
                listKey = 'afterInflateOrToUseList';
            }
            else if (status === CouponStatus.NotStart) {
                listKey = 'notStartList';
            }
            else if (status === CouponStatus.SoldOut) {
                listKey = 'soldOutList';
            }
            else if (status === CouponStatus.Used) {
                listKey = 'usedList';
            }
            else {
                listKey = 'defaultList';
            }
            if (valueType === ECouponType.DYNAMIC_AMOUNT_COUPON && beforeClaimeStatusList.includes(status)) {
                statusList[listKey].push(couponInfo);
            }
            else {
                statusList[listKey].unshift(couponInfo);
            }
        }
        Object.keys(statusList).forEach(listKey => {
            const couponList = statusList[listKey] || [];
            return couponList.sort((a, b) => {
                if (a.valueType === ECouponType.DYNAMIC_AMOUNT_COUPON && beforeClaimeStatusList.includes(a.status) || b.valueType === ECouponType.DYNAMIC_AMOUNT_COUPON && beforeClaimeStatusList.includes(a.status)) {
                    return 0;
                }
                // 计算补贴率
                const aDiscountAmount = Number(a.discountAmount);
                const aPriceLimit = Number(a.priceLimit);
                const bDiscountAmount = Number(b.discountAmount);
                const bPriceLimit = Number(b.priceLimit);
                const subsidyRateA = parseFloat((aDiscountAmount / aPriceLimit).toFixed(4));
                const subsidyRateB = parseFloat((bDiscountAmount / bPriceLimit).toFixed(4));
                // 比较补贴率
                if (subsidyRateA !== subsidyRateB) {
                    return subsidyRateB - subsidyRateA; // 降序排序
                }
                // 补贴率相同，比较券面值
                if (aDiscountAmount !== bDiscountAmount) {
                    return bDiscountAmount - aDiscountAmount; // 降序排序
                }
                // 券面值相同，比较门槛
                if (aPriceLimit !== bPriceLimit) {
                    return aPriceLimit - bPriceLimit; // 升序排序
                }
                // 门槛相同，比较失效日期
                const endTimeA = Number(a.endTime);
                const endTimeB = Number(b.endTime);
                return endTimeA - endTimeB; // 升序排序，由近到远
            });
        });
        return [...statusList.canInflateList, ...statusList.notClaimedList, ...statusList.afterInflateOrToUseList, ...statusList.notStartList, ...statusList.soldOutList, ...statusList.usedList, ...statusList.defaultList];
    }
    catch (e) {
        console.error(e);
        return couponList;
    }
};
// 过滤已结束、已过期、除动态满减券及满减券外的券类型，并按照配置券密钥的顺序排序
export const formatCouponList = (couponList, extendMsg, campaignKeys, currentTimeMillis) => {
    const result = {};
    couponList.forEach(({ couponInfoList, campaignKey }) => {
        (couponInfoList || []).forEach(couponInfo => {
            const { valueType, validateCode } = couponInfo || {};
            const status = getCouponStatus(couponInfo);
            // 已结束、已过期券不展示，后端接口返回时会做过滤，仅支持满减券或动态满减券，券查询失败时则过滤
            if ([ECouponType.COMMON_COUPON, ECouponType.DYNAMIC_AMOUNT_COUPON].includes(valueType) && status !== CouponStatus.Ended && status !== CouponStatus.Expired && validateCode !== EResultCode.RENDER_FAILED) {
                const couponExtendInfo = extendMsg[campaignKey] || {};
                const resultCouponInfo = Object.assign(Object.assign(Object.assign({ campaignKey,
                    status }, couponExtendInfo), couponInfo), { validateCode: validateCode || 0 });
                // 已领取不可膨胀或者已领取已膨胀是否生效
                if (hasValidStateStatusList.includes(status)) {
                    const isValid = resultCouponInfo.beginTime <= currentTimeMillis;
                    resultCouponInfo.isValid = isValid;
                }
                result[campaignKey] = Array.isArray(result[campaignKey]) ? [...result[campaignKey], resultCouponInfo] : [resultCouponInfo];
            }
        });
    });
    // 按照配置的keys顺序排列
    const orderCouponList = campaignKeys.reduce((prev, campaignKey) => {
        const value = result[campaignKey] ? result[campaignKey] : [];
        return prev.concat(value);
    }, []);
    return orderCouponList;
};
export const getPriceText = (couponInfo) => {
    const { hasCoupon, valueType, discountAmount, status } = couponInfo || {};
    if (!hasCoupon && valueType === ECouponType.DYNAMIC_AMOUNT_COUPON) {
        return '?';
    }
    if (status === CouponStatus.NotStart) {
        return '?';
    }
    return discountAmount;
};
export const getDescText = (couponInfo) => {
    const { valueType, discountAmount, priceLimit, hasCoupon } = couponInfo || {};
    if (!hasCoupon && valueType === ECouponType.DYNAMIC_AMOUNT_COUPON) {
        return discountAmount ? `最高${discountAmount}元` : '';
    }
    if (priceLimit === '0' || !priceLimit)
        return '无门槛';
    return priceLimit ? `满${priceLimit}可用` : '';
};
// 获取有效期描述
export function getMultiLimitTimeText(endTime, startTime, status, isValid) {
    if (!endTime || !startTime)
        return '';
    // 当前时间
    const now = startTime ? dayjs(Number(startTime)) : dayjs();
    // 过期时间
    const expiryDate = dayjs(Number(endTime));
    // 券过期日距离当前日期的差值（毫秒）
    const timeDiff = expiryDate.diff(now, 'millisecond');
    const dayDiff = Math.ceil(timeDiff / (24 * 60 * 60 * 1000));
    const floorDiff = Math.floor(timeDiff / (24 * 60 * 60 * 1000));
    const year = expiryDate.year() !== now.year() ? `${expiryDate.format('YYYY')}/` : '';
    const month = expiryDate.month() + 1;
    const day = expiryDate.date();
    if (timeDiff <= 0)
        return '';
    if (hasValidStateStatusList.includes(status) && !isValid) {
        if (dayDiff <= 1) {
            return timeDiff;
        }
        else {
            return `${dayDiff}天后可用`;
        }
    }
    if (status === CouponStatus.NotClaimed) {
        return `${year}${month}/${day}前可领`;
    }
    const notStartText = status === CouponStatus.NotStart ? '可领' : '';
    if (dayDiff > 1 && dayDiff <= 3) {
        return `${floorDiff + 1}天后${notStartText || '过期'}`;
    }
    else if (dayDiff <= 1) {
        return timeDiff;
    }
    else {
        return `${year}${month}/${day}${notStartText || '到期'}`;
    }
}
export const getCouponConfigMsg = couponConfig => {
    const configObject = {};
    (couponConfig || []).forEach(item => {
        let { couponKey, inflateBiz, maxInflateAmount, bizLine } = item || {};
        if (couponKey && inflateBiz) {
            configObject[couponKey] = {
                nibBiz: inflateBiz,
                maxInflateAmount,
                bizLine
            };
        }
    });
    return configObject;
};
